import type { ComputedRef, Ref } from "vue";
import { computed, ref, watch } from "vue";
import type { WellsQuery, WellsQueryVariables, Wells_Order_By } from "gql/api";
import { Order_By, useWellsQuery } from "gql/api";
import {
  wellsByUser,
  wellsByUwiorName,
  withoutTbdWell
} from "@/models/wells/predicates";
import { sortHandler } from "@/composables/primevue/tables";
import useUserInfo from "@/composables/auth/useUserInfo";

interface AdvancedSearchArgs {
  filterSizings: Ref<boolean>;
}

interface AdvancedSearch {
  allWellsResults: Ref<WellsQuery | undefined>;
  fetching: Ref<boolean>;
  searchText: Ref<string>;
  variables: ComputedRef<WellsQueryVariables>;
  itemsPerPage: Ref<number>;
  itemsPageNum: Ref<number>;
  handleSort: unknown;
}

export const useAdvancedSearch = ({
  filterSizings
}: AdvancedSearchArgs): AdvancedSearch => {
  const searchText = ref<string>("");
  const user = useUserInfo();
  const itemsPerPage = ref(20);
  const itemsPageNum = ref(0);
  const sortField = ref<Wells_Order_By[]>([{ well_name: Order_By.Asc }]);
  const variables = computed<WellsQueryVariables>(() => {
    if (filterSizings.value) {
      return {
        withAggregate: true,
        withSizings: true,
        limit: itemsPerPage.value,
        offset: itemsPerPage.value * itemsPageNum.value,
        order_by: sortField.value,
        where: {
          _or: [wellsByUwiorName(searchText.value)],
          _and: [wellsByUser(user.id as string), withoutTbdWell()]
        }
      };
    }
    return {
      withAggregate: true,
      withSizings: true,
      limit: itemsPerPage.value,
      offset: itemsPerPage.value * itemsPageNum.value,
      order_by: sortField.value,
      where: {
        _and: [wellsByUwiorName(searchText.value), withoutTbdWell()]
      }
    };
  });

  const { data: allWellsResults, fetching } = useWellsQuery({
    variables
  });

  watch([searchText], () => (itemsPageNum.value = 0));

  const handleSort = sortHandler({ itemsPageNum, sortField });

  return {
    allWellsResults,
    fetching,
    searchText,
    variables,
    itemsPerPage,
    itemsPageNum,
    handleSort
  };
};
